import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { fetchMyTicket, postMyTicketMessage } from '@/lib/support/tickets';
import { cn } from '@/lib/cn';
import { useAuthStore } from '@/stores/authStore';

export default function TicketDetailPage() {
  const { id = '' } = useParams();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const userId = useAuthStore((s) => s.user?.id);
  const [reply, setReply] = useState('');

  const { data: ticket, isLoading, isError } = useQuery({
    queryKey: ['support', 'ticket', id],
    queryFn: () => fetchMyTicket(id),
    enabled: !!id,
  });

  const send = useMutation({
    mutationFn: (body: string) => postMyTicketMessage(id, body),
    onSuccess: () => {
      setReply('');
      qc.invalidateQueries({ queryKey: ['support', 'ticket', id] });
    },
    onError: () => toast.error(t('common.error')),
  });

  if (isLoading) return <p className="p-6 text-sm text-gray-500">{t('common.loading')}</p>;
  if (isError || !ticket) {
    return (
      <div className="m-6 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
        {t('common.error')}
      </div>
    );
  }

  const messages = ticket.messages ?? [];

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <Link to="/support" className="text-sm text-primary-700 hover:underline">← {t('support.title')}</Link>
      <h1 className="text-2xl font-bold text-gray-900">{ticket.subject}</h1>

      <div className="space-y-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        {messages.length === 0 && (
          <p className="text-sm text-gray-500">{t('common.noResults')}</p>
        )}
        {messages.map((m) => {
          const isMine = m.author?.id === userId || m.authorType === 'user';
          return (
            <div key={m.id} className={cn('flex', isMine ? 'justify-end' : 'justify-start')}>
              <div
                className={cn(
                  'max-w-[80%] rounded-2xl px-4 py-2 text-sm',
                  isMine ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-900',
                )}
              >
                <div className="whitespace-pre-wrap">{m.body}</div>
                <div className={cn('mt-1 text-[10px]', isMine ? 'text-primary-200' : 'text-gray-500')}>
                  {new Date(m.createdAt).toLocaleString()}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <textarea
          rows={4}
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          placeholder={t('support.reply')}
          className="w-full resize-none rounded-lg border border-gray-200 px-3 py-2 text-sm"
        />
        <div className="mt-2 flex justify-end">
          <button
            type="button"
            disabled={send.isPending || !reply.trim()}
            onClick={() => send.mutate(reply.trim())}
            className="rounded-lg bg-primary-700 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            {t('support.send')}
          </button>
        </div>
      </div>
    </div>
  );
}
